import { useEffect, useRef, useState } from "react";
import Request from "../../common/Request.jsx";
import {
  ONBOARDING_V2_STEP_LABELS,
  OnboardingV2Stage,
  OnboardingV2Step,
} from "./OnboardingV2Stage";
import { PersonaPicker } from "./PersonaPicker";
import { PERSONA_OPTIONS, PersonaId } from "./personaOptions";
import {
  GalleryPracticeStep,
  GalleryPracticeDetails,
  GalleryPracticeStepHandle,
} from "./GalleryPracticeStep";
import {
  GalleryAccountStep,
  GalleryAccountDetails,
  GalleryAccountStepHandle,
} from "./GalleryAccountStep";
import { DASHBOARD_PATH, GetStartedStep } from "./GetStartedStep";
import { resolveProUrl } from "../GetStartedScreen";
import { GALLERY_GOALS, GalleryGoalOption } from "./galleryGoals";
import { ARTIST_GOALS } from "./artistGoals";
import { COLLECTOR_GOALS } from "./collectorGoals";
import { JEWELRY_GOALS } from "./jewelryGoals";
import {
  ARTIST_PERSONA_COPY,
  COLLECTOR_PERSONA_COPY,
  GALLERY_PERSONA_COPY,
  JEWELRY_PERSONA_COPY,
  PersonaCopy,
} from "./personaCopy";
import { ANNUAL_SALES, CAREER_STAGES, CareerStageCode, RevenueBandCode } from "../profileOptions";
import {
  ConvertedAccountType,
  emitAccountConverted,
  trackAccountConversion,
} from "./conversionTracking";

// ACA-266/267/268/269: the state machine that ties the four built screens together and talks to
// the server - §"Fase 4 - revista" and the "DECISÕES DE 23/09 (2ª sessão)" section of the plan for
// the gallery, Phase 5/6 of the ACA-267 plan for the artist branch, Phase 5 of the ACA-268 plan
// for the Collector branch added alongside it, and Phase 4 of the ACA-269 plan for Jewelry.
//
// All four personas render here as of ACA-269 Phase 4: Jewelry was the last one still handed to
// the caller (handlePersonaSelect's own gate, removed in that phase). onPersonaOutsideV2 has no
// live caller left inside this component because of it - it stays anyway (D6): it is the v2
// wizard's last exit to the legacy arm, and its own doc comment below explains why removing it
// would trade one dead branch for a bigger one.

export interface OnboardingV2Props {
  /**
   * UserType.Gallery's ordinal value - User.getValue('Gallery') in common/repository/Type.jsx.
   * Enters by prop from the .jsx bridge (page/Onboarding.jsx, Phase 5): TS cannot import that
   * file's named exports (global.d.ts only declares a default export for .jsx/.js - §2.6 of the
   * plan), and intentOptions.ts's `venue` already pays this same toll rather than duplicate the
   * enum as a TS literal that would silently drift if the C# ordinal ever changed.
   */
  galleryUserTypeValue: number;
  /**
   * UserType.Collector's ordinal value - User.getValue('Collector') === 6 in
   * common/repository/Type.jsx. Enters by prop for the same reason galleryUserTypeValue does: TS
   * cannot import that file's named exports. A sibling prop, not a map: generalising to
   * Partial<Record<PersonaId, number>> was this comment's own prediction of what ACA-269 would
   * need - wrong. Jewelry reuses galleryUserTypeValue (D2): a Jewelry account IS a Gallery account
   * (UserType 3) in the legacy sense, so it needs no ordinal of its own, only a
   * DefaultInventoryType - which is what jewelryInventoryTypeValue below carries. A sibling prop
   * turned out smaller than the map this comment once called for.
   */
  collectorUserTypeValue: number;
  /**
   * UserType.Jewelry has no ordinal of its own to enter by (D2: a Jewelry account IS a Gallery
   * account, UserType 3 - see galleryUserTypeValue's own doc comment). What it needs instead is
   * Inventory.Jewelry (5) from common/repository/Type.jsx, for the same reason as every other prop
   * on this interface: TS cannot import that file's named exports. Read only inside
   * handlePracticeContinue's Jewelry branch, where it becomes the fresh ghost's own
   * DefaultInventoryType on POST /register (D8's own matrix - the only endpoint that ever carries
   * it, per the plan's §10.3 armadilha).
   */
  jewelryInventoryTypeValue: number;
  /** ACA-269 Phase 4 left this prop with no live caller inside this component - Jewelry was the
      last persona still handed to the caller, and its own gate is gone (handlePersonaSelect no
      longer special-cases it). Kept anyway (D6): it is the v2 wizard's last exit to the legacy
      arm, and removing it would not remove any dead code - it would move INITIAL_TYPE_TO_USER_TYPE
      and the `props.initialType` branch in page/Onboarding.jsx from "reachable" to "dead", which is
      a bigger dead branch than this one, in a file this ticket is not touching. Named
      onPersonaOutsideV2, not onPersonaOutsideGallery: since Phase 5 it has meant "outside this
      component", not "outside the gallery persona" - every PersonaId this picker offers renders
      inside this component now. */
  onPersonaOutsideV2: (persona: PersonaId) => void;
  /**
   * A session cookie already exists and the settings GET it drives came back with a Type other
   * than Gallery - a ghost left behind by an earlier Artist or Collector pick, the only two
   * personas whose own Type.Name differs from "Gallery". A Jewelry ghost's Type.Name is coerced to
   * "Gallery" too (D7, the legacy's own getTypeDisplay) - so an existing Jewelry ghost never
   * reaches this callback at all; it passes the Type check below, and F5's own ghost recognition
   * (this phase) tells it apart from a plain gallery afterwards, by DefaultInventoryType instead.
   * See the note above the mount effect's Type check: a *fresh* Artist, Collector or Jewelry pick
   * stays in this component as of ACA-269 (Phase 5 already covered Artist and Collector), but an
   * *existing* Artist or Collector session still comes back through here.
   * Unlike onPersonaOutsideV2 this carries no PersonaId: the caller must render the legacy arm
   * with no initialType, so its own session branch takes over instead of initialType routing
   * through CreateAccountScreen's [AllowAnonymous] POST and minting a second account over the one
   * that already exists (measured: 1 -> 2 users).
   */
  onSessionPersonaMismatch: () => void;
  /**
   * ACA-267 defect 2 (B1): whether the session ViewBag.Session carried is a ghost's, read off
   * `Session.IsGhost` (Onboarding.cshtml). Three states, not a plain boolean: `false` means a real
   * account (redirect to Pro, see the mount effect), `true` means a ghost (today's behaviour,
   * unchanged), and absent means the server-side session read failed or returned nothing
   * (BaseController's own try/catch, ViewBag.Session left null) - in which case this component
   * falls back to today's behaviour too, the same as `true`. Optional on purpose: a required prop
   * here would need every existing call site updated together with a new type-check gate, which is
   * more than this fix asks for.
   */
  isGhost?: boolean;
}

interface RegistrationsResponseBody {
  /**
   * Lowercase, unlike every other response body in this file: this call alone goes through the
   * mkt server's own POST /register action (HomeController.PostRegisterAsync), which re-serialises
   * the API's PascalCase OnboardingRegistrationsResponse through mkt's own MVC JSON options -
   * ASP.NET Core's untouched default, camelCase. Settings/conversions below call the API directly,
   * where Startup.cs sets PropertyNamingPolicy = default and PascalCase survives unchanged.
   * Verified live: a gate-on /register run never advanced past step 2 until this was fixed.
   *
   * No Message field, on purpose (D2): re-serialising through RegistrationResponse
   * (Models/SessionModels.cs) drops anything the API sent beyond Session, on every status code -
   * this response can never carry a rejection reason, unlike settings/conversions below.
   */
  session?: string;
}

/**
 * The artist's POST /registrations/account response, kept as its own type rather than reusing
 * RegistrationsResponseBody above - this is the ARMADILHA the plan calls out. That type is
 * lowercase because the gallery's own /register call is re-serialised through mkt's own
 * HomeController.PostRegisterAsync into camelCase (see its own doc comment). The artist calls the
 * API directly - the same as settings and conversions below - where Startup.cs's untouched
 * PropertyNamingPolicy leaves PascalCase alone. Reusing the lowercase type here would compile (both
 * are `{ field?: string }`) and fail silently: `body.session` would always read undefined off this
 * response, and every call after it (profile, settings, complete) would go out with no
 * Authorization header.
 */
interface ArtistRegistrationResponseBody {
  Session?: string;
}

/** OnboardingSettingsTypeResponse - an EnumBase<UserType> - serialises to exactly this shape.
    Name is the display string ("Gallery", "Artist", "Collector", "Jewelry"): the same field
    Onboarding.jsx:253-255 reads off the identical response to decide who a returning ghost is. */
interface SettingsResponseBodyType {
  Name?: string;
  Value?: number;
}

interface SettingsResponseBody {
  BusinessName?: string;
  Intent?: number | null;
  Website?: string;
  CareerStage?: string;
  AnnualSales?: string;
  Type?: SettingsResponseBodyType | null;
  /**
   * ACA-269 F5 (D7): OnboardingController.cs:284-286 already returns this on every GET, gallery or
   * Jewelry alike - this type just did not declare it yet. The one field Type.Name does not carry:
   * a Jewelry account's own Type.Name is coerced to "Gallery" (the legacy's own getTypeDisplay,
   * Type.jsx:45-59), so this is what tells a returning Jewelry ghost apart from a plain one. The
   * same shape as Type above, reused rather than declared as a new type - both are the same
   * `{ Name?, Value? }` an EnumBase<T> serialises to.
   */
  DefaultInventoryType?: SettingsResponseBodyType | null;
}

interface WriteResponseBody {
  Message?: string;
}

interface ConversionsResponseBody {
  Message?: string;
}

interface ArtcloudCookies {
  get: (key: string) => string | undefined;
  remove: (key: string, attributes?: { domain?: string }) => void;
}

interface ArtcloudAuth {
  /** Onboarding.jsx's own `_clearOnboardingState` (:157-167) removes the journey cookie both
      plain and domain-scoped because Request.jsx's journey() mints it through Artcloud.cookie.set,
      whose default attributes stamp this domain - the same reason Artcloud.auth.logout() removes
      the session cookie twice, once per attribute shape it could have been set with. */
  domain: string;
  keys: { cookies: { session: string; onboardingId: string } };
  setSessionCookie: (session: string) => void;
}

interface ArtcloudFullstory {
  event?: (eventName: string, eventProperties: Record<string, unknown>) => void;
}

interface ArtcloudUrls {
  mkt?: { register?: () => string };
  vendor?: {
    onboarding?: {
      registrations?: { account?: { post?: () => string } };
      profile?: { put?: () => string };
      settings?: { get?: () => string; put?: () => string };
      complete?: { post?: () => string };
      conversions?: { post?: () => string };
    };
  };
}

interface ArtcloudUtil {
  /** Onboarding.jsx:857-863 and :656-660 both read query parameters through this - never
      window.location directly. Returns "" for an absent or empty one, never null/undefined
      (artcloud-mkt.js's own implementation). */
  getparameterbyname: (name: string) => string;
}

interface ArtcloudGlobal {
  cookie: ArtcloudCookies;
  auth: ArtcloudAuth;
  fullstory?: ArtcloudFullstory;
  url?: ArtcloudUrls;
  util?: ArtcloudUtil;
}

function artcloud(): ArtcloudGlobal | undefined {
  return typeof window === "undefined"
    ? undefined
    : (window as unknown as { Artcloud?: ArtcloudGlobal }).Artcloud;
}

function currentSession(): string | undefined {
  const client = artcloud();
  return client?.cookie.get(client.auth.keys.cookies.session) || undefined;
}

function persistSession(session: string): void {
  artcloud()?.auth.setSessionCookie(session);
}

function emitFullstoryEvent(name: string, properties: Record<string, unknown>): void {
  artcloud()?.fullstory?.event?.(name, properties);
}

/** Onboarding.jsx:859's own read (`Artcloud.util.getparameterbyname(param)`) folded together with
    :860's truthy check (`if (val)`): an absent or empty parameter comes back "" from
    getparameterbyname, never null/undefined, and this turns that into `undefined` too - "nothing
    was supplied" - rather than a value worth prefilling a field with. */
function queryParam(name: string): string | undefined {
  return artcloud()?.util?.getparameterbyname(name) || undefined;
}

/**
 * ACA-267 defect 2 (B1): the same house pattern as Onboarding.jsx:510's `window.location =
 * responseData.RedirectUrl` (Claim.jsx:58 and PasswordReset.jsx:30 do the same), `.href` rather
 * than reassigning `location` itself because this file is TypeScript and `Location` has no string
 * setter. There is no navigation seam in this codebase to route through instead (§0 of the plan
 * cuts adding one), so this call is untestable in Jest and is verified live instead.
 */
function navigateToPro(): void {
  if (typeof window !== "undefined") {
    window.location.href = resolveProUrl(DASHBOARD_PATH);
  }
}

/**
 * Onboarding.jsx:157-167's cookie half, ported verbatim - not the sessionStorage half. That
 * function also does `sessionStorage.removeItem(ONBOARDING_STATE_KEY)`, but ONBOARDING_STATE_KEY
 * ('ac_onboarding') is the legacy's own persisted-step key for its multi-page journey; this wizard
 * is one component tree that never writes to sessionStorage, so there is nothing there to clear.
 * Two cookie removes, matching the legacy exactly: the journey cookie can be minted two ways
 * (Request.jsx's journey() calls Artcloud.cookie.set with no explicit attributes, which stamps
 * this domain by default) so a plain remove alone can miss it, the same reason
 * Artcloud.auth.logout() removes the session cookie twice.
 */
function clearOnboardingJourney(): void {
  const client = artcloud();
  if (!client) {
    return;
  }
  client.cookie.remove(client.auth.keys.cookies.onboardingId);
  client.cookie.remove(client.auth.keys.cookies.onboardingId, { domain: client.auth.domain });
}

function resolveUrl(
  fromArtcloud: (client: ArtcloudGlobal) => string | undefined,
  fallback: string,
): string {
  const client = artcloud();
  return (client && fromArtcloud(client)) || fallback;
}

function registerUrl(): string {
  return resolveUrl((c) => c.url?.mkt?.register?.(), "/register");
}

function settingsGetUrl(): string {
  return resolveUrl(
    (c) => c.url?.vendor?.onboarding?.settings?.get?.(),
    "/api/onboarding/settings",
  );
}

function settingsPutUrl(): string {
  return resolveUrl(
    (c) => c.url?.vendor?.onboarding?.settings?.put?.(),
    "/api/onboarding/settings",
  );
}

function conversionsPostUrl(): string {
  return resolveUrl(
    (c) => c.url?.vendor?.onboarding?.conversions?.post?.(),
    "/api/onboarding/conversions",
  );
}

/** CreateAccountScreen.tsx's own resolution for the same endpoint, ported: the artist calls it
    directly from this container instead of through that Mantine screen. */
function registrationsAccountUrl(): string {
  return resolveUrl(
    (c) => c.url?.vendor?.onboarding?.registrations?.account?.post?.(),
    "/api/onboarding/registrations/account",
  );
}

function profilePutUrl(): string {
  return resolveUrl(
    (c) => c.url?.vendor?.onboarding?.profile?.put?.(),
    "/api/onboarding/profile",
  );
}

function completePostUrl(): string {
  return resolveUrl(
    (c) => c.url?.vendor?.onboarding?.complete?.post?.(),
    "/api/onboarding/complete",
  );
}

/**
 * GET has no counterpart in common/Request.jsx (only post/put), so this borrows its header
 * convention - Request.header.build(), the same one Request.post/put use - rather than
 * inventing a second way to assemble them.
 */
async function getSettings(): Promise<{
  status: number;
  body: SettingsResponseBody | null;
}> {
  try {
    const response = await fetch(settingsGetUrl(), {
      method: "GET",
      headers: Request.header.build(),
    });
    let body: SettingsResponseBody | null = null;
    try {
      body = await response.json();
    } catch (_) {
      /* no body */
    }
    return { status: response.status, body };
  } catch (_) {
    return { status: 0, body: null };
  }
}

/**
 * The reverse of §"Reconstruir pickedLabels na reidratação": a bitmask carries no click order, so
 * a rehydrated intent falls back to the table's own canonical order. Parametrized on `goals`
 * (Phase 5) so the artist's own table can feed it too. Rehydration was gallery-only through Phase
 * 5; ACA-269 F5 gave Jewelry the same path - the mount effect below calls this with JEWELRY_GOALS
 * once a ghost is recognised as Jewelry (`isJewelryGhost`, below), and with GALLERY_GOALS
 * otherwise. Artist and Collector ghosts still never reach this call: the mount effect hands them
 * to onSessionPersonaMismatch first (see §7 of the plan), which is the boundary this function's
 * own export exists to let a direct test pin, rather than one that would need a rehydration path
 * this component does not have for those two personas.
 */
export function pickedLabelsFromIntent(
  intent: number | null | undefined,
  goals: readonly GalleryGoalOption[],
): string[] {
  if (!intent) return [];
  return goals.filter((goal) => (intent & goal.value) !== 0).map((goal) => goal.label);
}

/**
 * ACA-269 F6 (29/09, two findings taken to the user after F5's own live validation): what a
 * returning ghost's practiceDetails recovers beyond intent/pickedLabels, which
 * pickedLabelsFromIntent above already covers. Jewelry is the only persona that both mints its
 * ghost AND collects BusinessName/Website/CareerStage/AnnualSales on step 2 (practiceFields,
 * careerFields - D1/§7.2(a)), yet before this phase none of the four came back on a reload:
 * BusinessName/Website only ever reached the server on step 3 (D8), so a visitor who left mid-flow
 * had a real ghost with no business name on it at all - the server never received it - and
 * CareerStage/AnnualSales, though written on step 2's own PUT since F4, were never read back into
 * `practiceDetails` the way Intent already is. The legacy's own `setUser(responseData)`
 * (page/Onboarding.jsx:320) has no such gap: it replaces the whole record, not a hand-picked subset
 * of it - matching that is the user's own instruction (29/09), not a new feature.
 *
 * Gated on `isJewelryGhost`, not on persona alone: the mount effect below is the only caller, and a
 * plain Gallery ghost - the only other shape that reaches this same rehydration (Artist/Collector
 * ghosts are handed to onSessionPersonaMismatch first, per pickedLabelsFromIntent's own doc
 * comment) - must keep the exact `{ intent, pickedLabels }` shape it already had.
 * GalleryPracticeStep's own comment above its `details()` builder warns why: the gallery's own
 * callers in OnboardingV2.test.tsx match the PUT body with `toHaveBeenCalledWith`, which fails on
 * any extra key riding along unseen in `initialValues`.
 *
 * CareerStage/AnnualSales are validated against profileOptions.ts's own lists rather than cast
 * straight through: the GET response types them as plain `string`, and a code the screen does not
 * recognise would otherwise sit in a <select> with no matching <option> - wrong and invisible at
 * once. Dropping it to `undefined` is what GalleryPracticeStep's own `careerStage`/`annualSales`
 * state already treats as "nothing chosen".
 */
export function jewelryGhostExtraFields(
  data: Pick<SettingsResponseBody, "BusinessName" | "Website" | "CareerStage" | "AnnualSales">,
  isJewelryGhost: boolean,
): Pick<GalleryPracticeDetails, "studioName" | "website" | "careerStage" | "annualSales"> {
  if (!isJewelryGhost) return {};
  const careerStage = CAREER_STAGES.some((option) => option.value === data.CareerStage)
    ? (data.CareerStage as CareerStageCode)
    : undefined;
  const annualSales = ANNUAL_SALES.some((option) => option.value === data.AnnualSales)
    ? (data.AnnualSales as RevenueBandCode)
    : undefined;
  return {
    studioName: data.BusinessName || undefined,
    website: data.Website || undefined,
    careerStage,
    annualSales,
  };
}

/**
 * The query string's ?businessName=/?website= applied to the step that actually shows them (PR
 * review, 30/09). The mount effect below writes all five marketing params into `accountDetails`,
 * which is where the legacy's own prefill lands them (RegistrationForm, Onboarding.jsx:893-903)
 * and where the gallery still reads them from. Jewelry moved that pair to step 2 - practiceFields
 * true, accountOrgFields false - so for it the prefill was landing in a field no screen renders
 * and no request reads: it reached `accountDetails.galleryName`, and `businessFieldsFor` sends
 * Jewelry's pair from `practiceDetails`. The value was simply dropped. The legacy has no such gap:
 * it has one form, the pair renders on it for everyone but the Collector (:1022-1042), and the
 * prefill fills it.
 *
 * `||`, not overwrite, is the legacy's own rule (`_data[field] = _data[field] || val`, :899): the
 * query only fills what is empty, and an answer already given wins. Here that answer can only be
 * one typed in this session, never a ghost's own record - with a session the picker is off the
 * tracker for good (`hasSession`) and this never runs, so a returning Jewelry ghost still takes
 * its organisation from jewelryGhostExtraFields above and nothing here can reach it.
 *
 * Returns `details` unchanged for every other persona: the gallery's pair is already on the screen
 * the mount effect prefills, and the Collector's is not asked for at all.
 */
export function organisationPrefillFor(
  persona: PersonaId,
  details: GalleryPracticeDetails | undefined,
  query: { businessName?: string; website?: string },
): GalleryPracticeDetails | undefined {
  if (persona !== "jewelry") {
    return details;
  }
  const studioName = details?.studioName || query.businessName;
  const website = details?.website || query.website;
  if (!studioName && !website) {
    return details;
  }
  // intent/pickedLabels are required by the interface and `undefined`/`[]` is what the step reads
  // as "nothing picked" (its own seededIntent/seededLabels), so seeding the organisation alone
  // leaves the goals exactly as unanswered as `undefined` did.
  return {
    ...details,
    intent: details?.intent ?? null,
    pickedLabels: details?.pickedLabels ?? [],
    studioName,
    website,
  };
}

/**
 * The picker's chip labels ("Send email campaigns") are not the legacy 'OB: Intent Declared'
 * vocabulary ("Email Campaign") - each goal table's own `analyticsLabel` carries the second one. A
 * label with no match (should not happen; every goal a step can hand back comes from the same
 * table passed in here) falls back to itself rather than dropping silently. Parametrized on
 * `goals` (Phase 5): handlePracticeContinue's shared gallery/Jewelry branch passes `copy.goals`
 * (ACA-269 - GALLERY_GOALS for the gallery, JEWELRY_GOALS for Jewelry, off the same `copy` the
 * render already computed), and handleAccountSubmit's artist branch passes ARTIST_GOALS - without
 * this, a persona whose own bits are not in whichever table gets hardcoded would speak the wrong
 * vocabulary for them, or (Jewelry's own two new bits, 8192/16384) fall back to the chip's own
 * label instead of an analyticsLabel at all. Exported for the same reason as
 * pickedLabelsFromIntent above.
 */
export function analyticsLabelsFor(
  labels: readonly string[],
  goals: readonly GalleryGoalOption[],
): string[] {
  return labels.map((label) => goals.find((goal) => goal.label === label)?.analyticsLabel ?? label);
}

function firstNameOf(fullName: string): string | undefined {
  const trimmed = fullName.trim();
  return trimmed ? trimmed.split(/\s+/)[0] : undefined;
}

/**
 * The BusinessName/Website pair POST /conversions sends, by persona (D8, ACA-269's own matrix -
 * plan §"F4", point 4). Three personas, three origins for what reads as one pair in the body
 * below:
 * - the gallery collects it on THIS screen (GalleryAccountStep's own Gallery Name/Gallery Website
 *   fields, `accountOrgFields` true), so `details` already carries it - unchanged from before
 *   ACA-269.
 * - the Collector never sends it here at all (D1, ACA-268): its own pair reaches the server one
 *   call earlier, on the settings PUT inside handleAccountSubmit's Collector branch. The literal
 *   `undefined` here is deliberate, not "whatever details.galleryName holds" - that would resend
 *   it on this call too and risk the spam-filter regression D8's own history describes (the
 *   Collector's BusinessName was briefly sent here before ACA-268's own fix, 20d131d82f, moved it
 *   off).
 * - Jewelry collects it on step 2 instead (GalleryPracticeStep's own organisation section,
 *   `practiceFields` true - D1), the same step the artist's Studio Name/Artist Website live
 *   behind. `accountOrgFields` is false for it (JEWELRY_PERSONA_COPY), so GalleryAccountStep never
 *   renders the fields `details.galleryName`/`details.website` would otherwise carry - both come
 *   back empty from this screen, which is NOT the same thing as "Jewelry sends nothing". The value
 *   lives in `practiceDetails` instead, the same place the artist's own profile PUT already reads
 *   StudioName/Website from. Since F6, `practiceDetails.studioName`/`website` also already reached
 *   the server once, on step 2's own settings PUT (handlePracticeContinue) - this call still sends
 *   the same pair again here, unchanged: that PUT closes the window where a reload could lose an
 *   unsent name, it does not replace this call, and this is still the one the anti-abuse filters
 *   (episodeRegex/DoesIndicateBadActorCompany) actually see, exactly as the legacy does.
 * Extracted rather than a bare ternary, per the plan: three origins in one conditional expression
 * stopped being something a reviewer could check at a glance.
 */
function businessFieldsFor(
  persona: PersonaId | undefined,
  details: GalleryAccountDetails,
  practice: GalleryPracticeDetails | undefined,
): { businessName: string | undefined; website: string | undefined } {
  if (persona === "collector") {
    return { businessName: undefined, website: undefined };
  }
  if (persona === "jewelry") {
    return { businessName: practice?.studioName, website: practice?.website };
  }
  return { businessName: details.galleryName, website: details.website };
}

const GENERIC_ERROR =
  "There was an error with your request. Please wait a moment and try again.";

/** CreateAccountScreen.tsx's own ERROR_MESSAGES.duplicateEmail, verbatim - the live screen's copy
    for a 409 on this exact endpoint. */
const DUPLICATE_EMAIL_ERROR = "That email address is currently in use. Please sign in.";

/** conversionTracking.ts's own ARTIST_CONVERSION_TAG, duplicated rather than imported: that file
    does not export it (it is only ever read from inside trackAccountConversion's switch), and this
    call site deliberately does not go through trackAccountConversion - see the comment on its call
    site in handleAccountSubmit for why. */
const ARTIST_CONVERSION_TAG = "AW-988451379/Swi2COyot6MYELOkqtcD";

/**
 * Onboarding.jsx:579-583, called directly instead of through trackAccountConversion: that helper
 * holds a state transition in gtag's own event_callback, and firing this tag is fire-and-forget
 * here - the profile/settings/complete calls that follow account creation must run whether or not
 * gtag ever calls back, unlike the gallery's redirect further down, which does wait on one.
 */
function fireArtistConversionTag(): void {
  const gtag =
    typeof window === "undefined"
      ? undefined
      : (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag === "function") {
    gtag("event", "conversion", { send_to: ARTIST_CONVERSION_TAG });
  }
}

/**
 * trackAccountConversion wants the legacy's own vocabulary (Onboarding.jsx:492 reads
 * user.Type.Name) - PERSONA_OPTIONS' labels already are that vocabulary, letter for letter, so
 * this is a lookup, not a translation. Exported for its own test.
 *
 * Two personas reach this call site as of ACA-268 Phase 5, not one: the gallery branch of
 * handleAccountSubmit, and now the Collector branch too, which falls through into that same shared
 * /conversions block rather than returning early (see that branch's own comment on why). That
 * fall-through is deliberate, not an oversight - this function returning "Collector" is what makes
 * conversionTracking.ts's own switch hit `case "Collector": onComplete(); break;` and skip the
 * Google Ads tag, exactly like the legacy Collector flow does. Only the artist branch (added in
 * Phase 5 alongside it) still returns before reaching here, since the artist never calls
 * trackAccountConversion at all (see that branch's own comment on its beacons). The fallback
 * "Gallery" below stays unreachable by a real persona either way - every persona that reaches this
 * call site is one PERSONA_OPTIONS already has a label for.
 */
export function accountConversionType(id: PersonaId | undefined): ConvertedAccountType {
  return PERSONA_OPTIONS.find((option) => option.id === id)?.label ?? "Gallery";
}

/**
 * All four personas render inside this component as of ACA-269 Phase 4 (Jewelry joins Gallery,
 * Artist and Collector). A switch replaces what used to be a ternary on `persona === "artist"` -
 * a fourth nested ternary would have made the branches unreadable as one expression.
 * GALLERY_PERSONA_COPY is still the explicit default rather than a bare fallback: returning it for
 * the gallery case here is what keeps that table the one place the gallery's own copy lives,
 * rather than leaving it to double as GalleryPracticeStep's/GalleryAccountStep's own defaults - a
 * safety net for a caller that forgets to pass anything, not a second source that could drift from
 * this one. That same `default:` is also the reason a missing `case "jewelry"` here would compile
 * and silently serve the gallery's own copy instead - TypeScript cannot flag an unreachable case a
 * switch never had. Exported so OnboardingV2.test.tsx can pin that directly (a render test cannot
 * stand in for it: any render test still passes if this silently serves GALLERY_PERSONA_COPY for
 * "jewelry", so long as nothing else on screen happens to look different - which is exactly the
 * failure mode this exists to catch).
 */
export function personaCopyFor(persona: PersonaId | undefined): PersonaCopy {
  switch (persona) {
    case "artist":
      return ARTIST_PERSONA_COPY;
    case "collector":
      return COLLECTOR_PERSONA_COPY;
    case "jewelry":
      return JEWELRY_PERSONA_COPY;
    default:
      return GALLERY_PERSONA_COPY;
  }
}

export function OnboardingV2({
  galleryUserTypeValue,
  collectorUserTypeValue,
  jewelryInventoryTypeValue,
  // onPersonaOutsideV2 is not destructured: nothing in this function body calls it any more (see
  // the interface's own doc comment above, and the file header note - D6 keeps the prop itself,
  // the interface member and app-shared.jsx's wiring; none of that needs a local binding here).
  onSessionPersonaMismatch,
  isGhost,
}: OnboardingV2Props) {
  // Onboarding.jsx:208 starts `step` undefined and gates every screen on strict equality
  // (:512, :519, :538, ...) - so until its mount effect decides, it renders nothing at all
  // (measured: the legacy arm server-renders the empty string). Defaulting to 1 here painted the
  // picker - server-rendered, then repainted identically on the client's first frame - before
  // this component's own mount effect had any chance to find a session and move off it. Matching
  // the legacy's own start, not a network wait: the render guard below only cares that the first
  // real value is decided, and that decision is synchronous either way (see the effect).
  const [step, setStep] = useState<1 | 2 | 3 | 4 | undefined>(undefined);
  const [persona, setPersona] = useState<PersonaId | undefined>(undefined);
  // Starts false to match the server-rendered paint (no `window`, §"the picker is not a screen
  // this wizard shows when a session cookie exists" below) and flips inside the mount effect,
  // the same timing the picker-blocking rule itself depends on. Once true, the picker is off the
  // tracker for good and Practice's own Back chevron stops rendering - a session minted mid-flow
  // (handlePracticeContinue) sets it too, not just a ghost found on mount.
  const [hasSession, setHasSession] = useState(false);
  const [practiceDetails, setPracticeDetails] = useState<
    GalleryPracticeDetails | undefined
  >(undefined);
  const [practiceError, setPracticeError] = useState<string | undefined>(undefined);
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [accountDetails, setAccountDetails] = useState<
    GalleryAccountDetails | undefined
  >(undefined);
  const [accountError, setAccountError] = useState<string | undefined>(undefined);
  // ACA-267 Phase 6, coordinator review: the artist's 409 duplicate-email rejection is shown
  // inline under GalleryAccountStep's own email field (`emailError` prop), not in the `error`
  // banner - CreateAccountScreen.tsx's own treatment. Separate state from accountError so the two
  // can be cleared and set independently; the gallery branch never touches this one.
  const [accountEmailError, setAccountEmailError] = useState<string | undefined>(undefined);
  const [accountLoading, setAccountLoading] = useState(false);
  const [firstName, setFirstName] = useState<string | undefined>(undefined);
  const practiceSubmitting = useRef(false);
  const accountSubmitting = useRef(false);
  // ACA-267 FIX-2, shared by both account-creating branches since ACA-268 Phase 5: the token THIS
  // submission minted, not environment state. currentSession() reads a cookie, which can be true
  // for reasons that have nothing to do with this instance ever having registered - a session from
  // a second tab's login appearing mid-flow, most concretely - so a cookie read cannot answer "did
  // my own POST already succeed". This ref is the guard instead: it only ever gets set inside the
  // artist's or the Collector's own `!registeredSessionRef.current` block below, right after that
  // POST's 201, and it is never cleared by a later failure (accountSubmitting.current resets on
  // every fail() so a retry is possible; this does not, so retrying never re-registers). One ref
  // for both personas, not two: only one of them can be active in a given mount - persona is
  // single-valued - and two refs with the same meaning is how one of them ends up stale.
  const registeredSessionRef = useRef<string | undefined>(undefined);
  // D5: what the tracker's step links read to capture the active step's in-progress fields - the
  // same snapshot each step's own Back button already sends through onBack, since a tracker click
  // does not fire it.
  const practiceStepRef = useRef<GalleryPracticeStepHandle>(null);
  const accountStepRef = useRef<GalleryAccountStepHandle>(null);

  // The ghost that returns (§7.4 / the 2nd-session decisions): a session cookie on mount means
  // someone already has an account in progress. Reading it - and the GET it drives - has to
  // happen here and nowhere in render: Onboarding.cshtml renders this through Html.React, and a
  // `window` read during render breaks hydration.
  useEffect(() => {
    // RegistrationForm's own prefill (Onboarding.jsx:851-866). All five params land on the account
    // screen, which is where all five fields now live (24/09 - Gallery Name and Gallery Website
    // moved off Practice). It runs regardless of session there - it is a property of the account
    // form, not of how anyone arrived at it - so it runs unconditionally here too, before the
    // session branch below. A rehydrated ghost's own answers still win, but only where it HAS one:
    // rehydration (further down) merges over this rather than replacing it, which is the `|| val`
    // the legacy gets for free.
    const businessName = queryParam("businessName");
    const website = queryParam("website");
    const email = queryParam("email");
    const name = queryParam("name");
    const phone = queryParam("phone");
    if (businessName || website || email || name || phone) {
      setAccountDetails({
        email: email ?? "",
        fullName: name ?? "",
        galleryName: businessName ?? "",
        website: website ?? "",
        phone: phone ?? "",
        password: "",
        confirmPassword: "",
        howDidYouHear: "",
        hasAgreedToTerms: false,
      });
    }

    if (!currentSession()) {
      // WhoAreYou's own mount effect (Onboarding.jsx:656-660) only ever runs without a session -
      // Step.WhoAreYou is unreachable once one exists (:222) - so the ?userType= read is scoped
      // the same way here, not a new rule. Onboarding.jsx's setActive() only marks a box; nothing
      // advances until the visitor's own click on Continue (:662-671) - this picker has no
      // Continue (PersonaPicker.tsx's own comment: choosing a card moves on immediately), so
      // preselecting cannot mean "auto-advance to Practice" without inventing behaviour the
      // legacy does not have. setPersona() below is the faithful stop short of that: it is the
      // same prop PersonaPicker already uses to show an earlier choice without navigating (the
      // tracker's own "You" link relies on it too), applied here to a different source. The
      // visitor still has to click a card - the highlighted one or another - for anything to
      // happen, exactly as today. An absent or unrecognised value leaves nothing selected, the
      // same as every box staying unlit in the legacy.
      const userType = queryParam("userType");
      const preselected = PERSONA_OPTIONS.find((option) => option.label === userType);
      if (preselected) {
        setPersona(preselected.id);
      }
      // Onboarding.jsx:208 starts step undefined; :236 is its only sessionless assignment. This
      // wizard's own step no longer defaults to 1 in state (see above), so the sessionless case
      // has to make that assignment explicitly, the same way the legacy's own effect does.
      setStep(1);
      return;
    }
    // ACA-267 defect 2 (B1): a non-ghost session means a real account already exists, and the
    // three setters just below would paint the account screen over it - the artist's own is real
    // from birth (UserWarden.cs), so this is the case Onboarding.cshtml's isGhost prop exists to
    // catch before that first paint. It has to be here rather than after the settings GET, which
    // returns via the Type check below and would never reach a later test. `undefined` (the prop
    // never arrived, or ViewBag.Session was null) falls back to today's behaviour, same as `true`
    // - B2 is the backstop for that gap, not this component.
    if (isGhost === false) {
      navigateToPro();
      return;
    }
    // Legacy assumes, then adapts (Onboarding.jsx:265-270): step moves to Account *before* the
    // GET even starts, not after it resolves - the invariant is "never paint the persona choice
    // over a live identity", not "never paint until the GET answers". This also covers the
    // failure arm for free: onError there drops the loading backdrop but never returns to
    // Step.WhoAreYou, and skipping the setStep call below on a rejected/failed GET leaves this
    // exactly where it already put things - never back on the picker.
    setHasSession(true);
    setPersona("gallery");
    setStep(3);
    let cancelled = false;
    (async () => {
      const response = await getSettings();
      if (cancelled || response.status < 200 || response.status >= 300 || !response.body) {
        return;
      }
      const data = response.body;
      // The defect this guards: this rehydration used to decide "gallery" from the session cookie
      // alone and never looked at Type, the field Onboarding.jsx:253-255 reads off the identical
      // response to tell an Artist/Collector ghost apart from a Gallery one. A Jewelry ghost is not
      // one Type tells apart here: its own Type.Name is coerced to "Gallery" too (D7, the legacy's
      // own getTypeDisplay, Type.jsx:45-59), so it always passes this check the same as a plain
      // gallery does - telling the two apart takes a second field, DefaultInventoryType, read below
      // (F5). Only Gallery (and, once recognised, Jewelry) rehydrates here; anything else -
      // including an existing Artist ghost, which Phase 5 still does not adopt (§7 of the plan:
      // this component's Artist branch is built for someone with no account yet, and adopting one
      // would let a stale PUT /profile overwrite a real one) - goes back through the caller with no
      // PersonaId attached, so the hand-off must not go through initialType and mint a second
      // account.
      if (data.Type?.Name && data.Type.Name !== "Gallery") {
        onSessionPersonaMismatch();
        return;
      }
      // ACA-269 F5 (D7): a correction after the fact, not a replacement of the setPersona("gallery")
      // above - that one runs before this GET even starts (never paint the persona choice over a
      // live identity), and is deliberately left alone. DefaultInventoryType is the field the guard
      // above cannot read Jewelry off, because Type.Name was already coerced to "Gallery" by the
      // time this response left the server. jewelryInventoryTypeValue, not a literal 5: same reason
      // every other UserType/Inventory ordinal on this component enters by prop (D2).
      //
      // §10.4 of the plan: the account screen (step 3) already mounted with the gallery's own copy
      // before this ever runs - setStep(3) above does not wait for the GET. GalleryAccountStep's own
      // `key` (below, where it renders) is keyed on the account fields, not on persona, so this does
      // not remount it; its labels (emailPlaceholder, backLabel, ...) come from `copy`, computed
      // fresh from `persona` on every render, so they still update by prop on the very next render.
      // For as long as the GET is in flight - not "one frame", as this comment claimed until the
      // PR review measured it with the response held back - whoever picked Jewelry is looking at
      // the gallery's own email placeholder AND its two organisation inputs (accountOrgFields is
      // true for the gallery, false for Jewelry), which then disappear. Cosmetic, and still not
      // fixed here; the data loss that used to ride along with it is, just below.
      const isJewelryGhost =
        data.Type?.Name === "Gallery" &&
        data.DefaultInventoryType?.Value === jewelryInventoryTypeValue;
      if (isJewelryGhost) {
        setPersona("jewelry");
      }
      setPracticeDetails({
        intent: data.Intent ?? null,
        pickedLabels: pickedLabelsFromIntent(
          data.Intent,
          isJewelryGhost ? JEWELRY_GOALS : GALLERY_GOALS,
        ),
        // F6: studioName/website/careerStage/annualSales, only for a ghost recognised as Jewelry -
        // see jewelryGhostExtraFields' own doc comment for why the gate matters and what it
        // protects. A plain Gallery ghost gets `{}` here, so its practiceDetails keeps the exact
        // two-field shape it always had.
        ...jewelryGhostExtraFields(data, isJewelryGhost),
      });
      // Onboarding.jsx:861 fills a field the record leaves EMPTY (`_data[field] = _data[field] || val`)
      // instead of overwriting it, so a marketing link's ?businessName=/?website= still lands for a
      // ghost whose own record carries neither. Replacing outright dropped them - found driving QA,
      // 24/09. Only these two are merged: they are the only account fields the record can speak to,
      // and the rest of the screen (password, terms) is never rehydrated from the server at all.
      //
      // Skipped entirely for a Jewelry ghost (PR review, 30/09). BusinessName/Website are the only
      // two values this call takes from the record - every other field is `prefilled?.x`, the state
      // rewriting itself - and for Jewelry neither of them has a reader: accountOrgFields is false,
      // so step 3 never renders the inputs they would fill, and businessFieldsFor sends the pair
      // from `practiceDetails` instead (its own doc comment). What the call DID have was an effect:
      // `galleryName`/`website` are what GalleryAccountStep's `key` is built from, so landing a
      // BusinessName here changed the key and remounted the step, wiping whatever had been typed
      // into Email/Full Name/password while the GET was in flight. Harmless until F6, because the
      // pair only ever reached the server on step 3 and a returning ghost's record had neither;
      // once step 2 started PUTting BusinessName, every returning Jewelry ghost hit it. The legacy
      // cannot reach this at all - Onboarding.jsx:686 covers the screen with <ModalBackground />
      // for the whole GET, so there is no typing to lose, and it merges into the form's own state
      // rather than remounting it.
      if (!isJewelryGhost) {
        setAccountDetails((prefilled) => ({
          email: prefilled?.email ?? "",
          fullName: prefilled?.fullName ?? "",
          galleryName: data.BusinessName || prefilled?.galleryName || "",
          website: data.Website || prefilled?.website || "",
          phone: prefilled?.phone ?? "",
          password: prefilled?.password ?? "",
          confirmPassword: prefilled?.confirmPassword ?? "",
          howDidYouHear: prefilled?.howDidYouHear ?? "",
          hasAgreedToTerms: prefilled?.hasAgreedToTerms ?? false,
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Onboarding.jsx's own render gates every screen on `step === Step.X` with no fallback branch,
  // so an undecided (`undefined`) step there paints nothing - not the picker, not a blank stage
  // shell, nothing. The effect above always decides `step` synchronously (setStep(1) or (3)) in
  // its very first pass, so this is never a network wait - only the width of one mount.
  if (step === undefined) {
    return null;
  }

  // N9: practiceError/accountError were cleared on submit, never on navigation - so a rejected
  // attempt's message kept reappearing on the step it belonged to after a 2 -> 1 -> 2 or
  // 3 -> 2 -> 3 round trip, whichever route (Back button or tracker) made the trip.
  const clearErrors = () => {
    setPracticeError(undefined);
    setAccountError(undefined);
    setAccountEmailError(undefined);
  };

  const handlePersonaSelect = (id: PersonaId) => {
    const option = PERSONA_OPTIONS.find((candidate) => candidate.id === id);
    emitFullstoryEvent("OB: Persona Selected", { type: option?.label ?? id });
    clearErrors();
    // Read here rather than inside the updater below, which React is free to call more than once
    // and which has to stay pure. Reading it in this handler at all - rather than off the mount
    // effect's own read - is safe for the reason that read exists: the ban is on touching `window`
    // during render (hydration), and an event handler is not render. Nothing in this component
    // rewrites the URL between the two, so both see the same query string.
    const query = {
      businessName: queryParam("businessName"),
      website: queryParam("website"),
    };
    setPracticeDetails((current) => {
      // The goals are per persona: GALLERY_GOALS and ARTIST_GOALS share three bits (2, 4 and
      // 128), and JEWELRY_GOALS shares two more with the gallery's own table (16 and 32) - every
      // pair diverges on the rest, so whatever a previous pick left behind is not an answer to
      // this question. Re-picking the persona already selected is not a change and keeps it - the
      // ?userType= preselect lands on exactly that case, where `persona` is already set and the
      // visitor's click confirms it.
      const kept = id === persona ? current : undefined;
      return organisationPrefillFor(id, kept, query);
    });
    setPersona(id);
    setStep(2);
  };

  // Runs for Continue, which since 25/09 is the only way out of the practice step: the
  // "Skip for now" exit that used to funnel through this same callback with a null intent is
  // gone, so what arrives here is always a real bitmask.
  const handlePracticeContinue = async (details: GalleryPracticeDetails) => {
    if (practiceSubmitting.current) {
      return;
    }

    if (persona === "artist" || persona === "collector") {
      // Neither the artist's step 2 nor the Collector's makes an account-creation network call,
      // unlike the gallery's below (which mints or updates a server-side ghost right here) - but
      // for different reasons. The artist has no session yet to create one with: POST
      // registrations/account, which is what mints it, needs the email and password that only
      // exist on step 3. The Collector has no session yet either, but not because a field is
      // missing - its legacy flow (Onboarding.jsx:519-523) never creates a ghost until the step 3
      // submit, immediately before /conversions; the legacy Collector has no intent screen at all
      // (§3.1 of the plan), so there was never a step 2 network call to port. Every goal toggle and
      // Continue itself still fire useIntentSelection's own pre-existing beacon (POST
      // /api/onboarding/intents/events, intentEvents.ts) - analytics-only, and not what "no network
      // call" above means. 'OB: Intent Declared' is not emitted here either, for the same reason
      // the legacy never emits it before an account exists - IntentScreen for the artist
      // (Onboarding.jsx:436, reached only once an account exists), the step 3 submit itself for the
      // Collector - declaring an intent for an account that might never come to exist would be a
      // claim this flow cannot back up. It is emitted instead in handleAccountSubmit, right after
      // the write that carries this same Intent value.
      setPracticeDetails(details);
      setStep(3);
      return;
    }

    practiceSubmitting.current = true;
    setPracticeDetails(details);
    setPracticeLoading(true);
    setPracticeError(undefined);

    const fail = (message: string | undefined) => {
      practiceSubmitting.current = false;
      setPracticeLoading(false);
      setPracticeError(message || GENERIC_ERROR);
    };

    try {
      let session = currentSession();
      if (!session) {
        // D8/§10.3 of the plan: DefaultInventoryType rides on this call alone, and only for
        // Jewelry - neither the settings PUT below nor /conversions accepts it
        // (OnboardingRequests.cs:81-92 and :94-115). The gallery's own body must stay exactly what
        // it is today, so this only ever adds a key that was not there before, never one the
        // gallery branch of this same code path also sends.
        const registered = await Request.post(registerUrl(), {
          Type: galleryUserTypeValue,
          Intent: details.intent,
          ...(persona === "jewelry" ? { DefaultInventoryType: jewelryInventoryTypeValue } : {}),
        });
        const body = registered.body as RegistrationsResponseBody | null;
        if (registered.status !== 201 || !body?.session) {
          // Always the generic message (D2): RegistrationResponse never carries anything but
          // Session, so a server-written rejection reason cannot reach this branch.
          fail(undefined);
          return;
        }
        session = body.session;
        // Has to happen before the PUT below, or that call goes out without a session.
        persistSession(session);
      }
      // A server-side identity now exists, whether it was already there or was just minted above
      // - either way, the picker is no longer a place this flow can safely send anyone back to
      // (rule #4: it would offer a persona swap over a live ghost). Already true when a session
      // was found on mount; setting it again here is what covers the freshly-registered case.
      setHasSession(true);

      // Intent rides along on every PUT, not just the POST that mints the ghost - a second pass
      // through this step (goals changed, session already exists) is the only place that write
      // happens at all. The server seeds IntentAction per goal, not all-or-nothing (D4,
      // OnboardingController.cs:330-360), so a later pass adds rows for a newly picked goal and
      // leaves the goals already seeded alone.
      // Intent is all this step collects for the gallery now (24/09). The four organisation
      // fields it used to send are gone: two moved to the account screen, where the conversions
      // POST carries them, and CareerStage/AnnualSales are not asked during the gallery's signup
      // at all any more. Omitting them does not blank anything - PostSettingsAsync guards every
      // write (`if (careerStage.HasValue)`), so a record that already has them keeps them.
      //
      // Jewelry is the one persona sharing this branch that IS asked for CareerStage/AnnualSales
      // on this same step (§7.2(a)), so its own pass through this PUT carries them - D8's own
      // matrix. CareerStage/AnnualSales are sent with no conversion at all, on purpose (§10.3b's
      // armadilha): GalleryPracticeStep already produces the same string codes PostSettingsRequest
      // wants ("STARTING_OUT"/"EMERGING"/"PROFESSIONAL", "REV_5K_20K"/etc.) - translating them here
      // would be a second copy of the server's own MapCareerStage/MapAnnualSales that could drift.
      //
      // BusinessName/Website ride along here too, since F6 (29/09 - two findings taken to the user
      // after F5's own live validation, not this plan's original design). Before F6 the pair lived
      // only in `practiceDetails`, in browser memory, between this step and the /conversions POST
      // on step 3 (D8 as originally written): a visitor who reloaded or came back later had a real
      // ghost already minted on /register above, with no business name on it at all, because
      // nothing had sent one yet - jewelryGhostExtraFields' own doc comment is the other half of
      // this fix, reading the name back on a return visit. Sending it here does not replace
      // /conversions sending it too (unchanged - see businessFieldsFor's own doc comment): that
      // endpoint is still where episodeRegex/DoesIndicateBadActorCompany see it, exactly as the
      // legacy's own Jewelry form does. Writing the same value twice is harmless -
      // PostSettingsAsync and PostConversionsAsync each guard their own write
      // (`if (!string.IsNullOrWhiteSpace(...))`), so this is belt, not a second source of truth.
      const settled = await Request.put(settingsPutUrl(), {
        Intent: details.intent,
        ...(persona === "jewelry"
          ? {
              CareerStage: details.careerStage,
              AnnualSales: details.annualSales,
              BusinessName: details.studioName,
              Website: details.website,
            }
          : {}),
      });
      if (settled.status < 200 || settled.status >= 300) {
        fail((settled.body as WriteResponseBody | null)?.Message);
        return;
      }

      // copy.goals, not the GALLERY_GOALS literal this used to read directly: this branch is now
      // shared with Jewelry, whose two new bits (8192/16384) have no match in GALLERY_GOALS and
      // would fall back to the chip's own label instead of the funnel's analyticsLabel
      // (analyticsLabelsFor's own fallback). Safe to read `copy` from a handler defined above its
      // declaration further down in this render: the handler only ever executes after the render
      // that defines both has finished, by which point `copy` is already assigned - the same
      // closure timing every other handler below already relies on for state set later in render.
      emitFullstoryEvent("OB: Intent Declared", {
        intents: analyticsLabelsFor(details.pickedLabels, copy.goals),
      });
      practiceSubmitting.current = false;
      setPracticeLoading(false);
      setAccountError(undefined);
      setAccountEmailError(undefined);
      setStep(3);
    } catch (_) {
      fail(undefined);
    }
  };

  const handleAccountSubmit = async (details: GalleryAccountDetails) => {
    // N7: `disabled={loading}` alone races a second click against the re-render that disables the
    // button - the cost of losing that race here is a second conversions POST, and a second Google
    // Ads conversion tag firing for one signup. Same ref-guard pattern as practiceSubmitting.
    if (accountSubmitting.current) {
      return;
    }
    accountSubmitting.current = true;
    setAccountLoading(true);
    setAccountError(undefined);
    // Cleared unconditionally, not just on the 409 branch below: this is what lets the effect in
    // GalleryAccountStep re-show a fresh rejection even when its text is identical to the one just
    // dismissed - the prop goes undefined here, then the message again once the response arrives,
    // which is a value change that component's own effect can see.
    setAccountEmailError(undefined);

    const fail = (message: string | undefined) => {
      accountSubmitting.current = false;
      setAccountLoading(false);
      setAccountError(message || GENERIC_ERROR);
    };

    if (persona === "artist") {
      try {
        // Guard of its own, not copied from anywhere: CreateAccountScreen.handleSubmit (the shape
        // the call below otherwise follows) has none, and page/Onboarding.jsx:284-288's
        // similar-looking guard belongs to _createAccount, the gallery ghost's own helper - neither
        // is precedent for this one. Without it, a retry after the profile PUT or the settings PUT
        // below fails would re-run this POST and mint a second account over the one the first
        // attempt already created.
        //
        // ACA-267 FIX-2: reads registeredSessionRef (this instance's own minted token), not
        // currentSession()'s cookie. A cookie is environment state, not this submission's: it can
        // be truthy because a second tab logged in while this one sat on step 3, and reading it
        // here would silently send this tab's profile/settings PUTs onto that other account instead
        // of registering one of its own.
        //
        // Known and unresolved: once this has run, Email and Password on the screen behind it no
        // longer reach anything, and nothing on that screen says so. The legacy has no such state
        // - it creates the account on a screen that unmounts - and what to show here instead is a
        // product question, recorded on the PR rather than answered by inventing a screen.
        if (!registeredSessionRef.current) {
          const registered = await Request.post(registrationsAccountUrl(), {
            Email: details.email,
            Password: details.password,
            TcsAccepted: details.hasAgreedToTerms,
            IsArtistReferral: false,
          });

          if (registered.status === 409) {
            // Coordinator review decision: inline under the email field, GalleryAccountStep's own
            // `emailError` prop (added for exactly this) - the live screen's own treatment
            // (CreateAccountScreen.tsx's form.setFieldError("email", ...)). Not the generic banner.
            accountSubmitting.current = false;
            setAccountLoading(false);
            setAccountEmailError(DUPLICATE_EMAIL_ERROR);
            return;
          }

          const body = registered.body as ArtistRegistrationResponseBody | null;
          if (registered.status !== 201 || !body?.Session) {
            fail(undefined);
            return;
          }

          // Has to happen before the profile/settings PUTs below, or they go out with no session.
          persistSession(body.Session);
          // This instance's own record of having registered - the guard above reads this, not the
          // cookie persistSession just set. Assigned only after persistSession returns: arming the
          // guard first and having persistSession throw would leave this instance believing it
          // already has an account with no cookie behind it - unrecoverable, since every retry
          // after that would skip the POST above and send the profile/settings PUTs below out with
          // no Authorization header at all.
          registeredSessionRef.current = body.Session;

          // Onboarding.jsx:574-583's own two beacons, same order, fired once - guarded by the
          // `!registeredSessionRef.current` branch above so a retry never re-sends them. Not
          // trackAccountConversion: that call returns synchronously, and it is the *caller* that
          // holds a continuation up until gtag's callback fires, by placing it inside
          // event_callback - the gallery's own redirect further down does exactly that. Firing the
          // tag here is deliberately not wired that way: the writes below must run whether or not
          // gtag ever calls back. Source is always "organic": app-shared.jsx's own gate on this
          // whole component (`!props.isArtistReferral`) means an artist referral link never reaches
          // v2 to begin with, so "artlogic-artist" is not a value this branch can ever see -
          // confirmed against that condition rather than threaded as a prop that could only ever
          // hold one value.
          emitFullstoryEvent("OB: Artist Registered", { source: "organic" });
          fireArtistConversionTag();
        }
        // Mirrors the gallery's own setHasSession(true) below: already true when a session was
        // found on mount (not a case this branch reaches per §7 of the plan) or minted just above;
        // setting it again on a retry is harmless and keeps the two branches' shape the same.
        setHasSession(true);

        const profileResult = await Request.put(profilePutUrl(), {
          FullName: details.fullName,
          StudioName: practiceDetails?.studioName,
          Phone: details.phone,
          CareerStage: practiceDetails?.careerStage,
          AnnualSales: practiceDetails?.annualSales,
          HowDidYouHear: details.howDidYouHear,
        });
        if (profileResult.status < 200 || profileResult.status >= 300) {
          fail((profileResult.body as WriteResponseBody | null)?.Message);
          return;
        }

        const settingsResult = await Request.put(settingsPutUrl(), {
          Intent: practiceDetails?.intent,
          Website: practiceDetails?.website,
        });
        if (settingsResult.status < 200 || settingsResult.status >= 300) {
          fail((settingsResult.body as WriteResponseBody | null)?.Message);
          return;
        }

        // IntentScreen's own moment (Onboarding.jsx:436), not GalleryPracticeStep's Continue: the
        // legacy artist only ever declares an intent once the account it belongs to already exists
        // (see the comment in handlePracticeContinue). This is that moment's equivalent here,
        // right after the settings PUT that carries the same Intent value.
        emitFullstoryEvent("OB: Intent Declared", {
          intents: analyticsLabelsFor(practiceDetails?.pickedLabels ?? [], ARTIST_GOALS),
        });

        try {
          // Request.send never throws on an HTTP error status, but it does propagate a rejected
          // fetch - and /complete is fire-and-forget in the legacy too (Onboarding.jsx:385-386
          // gives it two identical callbacks for success and error). A bare await here would let
          // that rejection fall into the outer catch and show a "something went wrong" banner over
          // an account that, by this point, already exists.
          await Request.post(completePostUrl(), undefined);
        } catch (_) {
          /* advance regardless - see comment above */
        }

        // Onboarding.jsx:374-389's own _finishArtistOnboarding: goGetStarted starts with
        // _clearOnboardingState() before anything else, the same reason the gallery's own call
        // site above does it inside trackAccountConversion's callback - a second signup in this
        // browser must not reuse this journey's id. Not Onboarding.jsx:377's `ac_welcome` cookie,
        // for the same reason noted at the gallery's own call site: its only reader requires
        // Subscriptions.IsArtsySource, which no v2 signup - gallery or artist - can ever be. Only
        // the cookie half is ported here, same as clearOnboardingJourney's own doc comment already
        // says: _clearOnboardingState's sessionStorage.removeItem half has nothing to remove, since
        // this wizard never writes to sessionStorage in the first place.
        clearOnboardingJourney();
        accountSubmitting.current = false;
        setAccountLoading(false);
        setFirstName(firstNameOf(details.fullName));
        setStep(4);
      } catch (_) {
        fail(undefined);
      }
      return;
    }

    if (persona === "collector") {
      // Onboarding.jsx:519-523: the legacy Collector mints its ghost right here, immediately
      // before /conversions - never in step 2, because its legacy flow has no step 2 at all (no
      // intent screen at all - §3.1 of the plan). The Intent rides along on this same POST
      // (PostRegistrationsRequest.Intent -> newGallery.SignupIntent, UserWarden.cs:66, which also
      // seeds IntentAction). /register is not the only write this branch needs, though: since D1
      // (26/09 - see COLLECTOR_PERSONA_COPY's own comment) step 2 also collects Collection Name
      // and Website, and PostRegistrationsRequest has no room for either - the settings PUT below
      // is where they actually reach the server, unconditionally, not just on a retry.
      try {
        // Same FIX-2 guard the artist branch uses above, shared via registeredSessionRef: reads
        // this instance's own minted token, not currentSession()'s cookie, for the same reason - a
        // second tab's login must not send this tab's writes onto that other account.
        if (!registeredSessionRef.current) {
          const registered = await Request.post(registerUrl(), {
            Type: collectorUserTypeValue,
            Intent: practiceDetails?.intent,
          });
          // Lower-case `session`, deliberately: this call goes to the same mkt server POST
          // /register the gallery branch above uses, not to the API directly - so it reads the
          // same RegistrationsResponseBody, not ArtistRegistrationResponseBody's PascalCase
          // `Session`. RegistrationsResponseBody's own doc comment above spells out the trap: the
          // wrong type here compiles and fails silently, because both shapes are `{ field?:
          // string }`.
          const body = registered.body as RegistrationsResponseBody | null;
          if (registered.status !== 201 || !body?.session) {
            // Always the generic message (D2): RegistrationResponse never carries anything but
            // Session, so a server-written rejection reason cannot reach this branch.
            fail(undefined);
            return;
          }
          persistSession(body.session);
          registeredSessionRef.current = body.session;
          setHasSession(true);
        }

        // Unconditional on purpose (26/09 scope fix) - not the retry-only write this used to be
        // before D1 added the organisation fields. Sending it on every pass, first included,
        // is what gets the D2 guarantee below for free: this used to be an `else` branch, reached
        // only when registeredSessionRef.current was already set, because Intent was the only thing
        // this step collected and it already had a ride on the POST above - so a retry (the one
        // case /register does not run again) needed its own write, or a goal change made after a
        // failed /conversions would never reach the server (see the D2 test in
        // OnboardingV2.test.tsx for the scenario this protects). Now that BusinessName and Website
        // exist too, they have no POST to ride on at all - only this PUT - so the write has to run
        // on the first pass as much as on any retry, and the `else` that used to gate it on
        // "already registered" no longer describes anything real. The property it
        // protected still holds, now as a side effect rather than a dedicated branch: every pass
        // through this code, retry or not, PUTs the current Intent. PostSettingsAsync
        // (OnboardingController.cs) writes each field behind its own guard
        // (`if (!string.IsNullOrWhiteSpace(...))` / `.HasValue`), so sending it unconditionally,
        // including on a first pass where every field is already fresh, blanks nothing.
        const settled = await Request.put(settingsPutUrl(), {
          Intent: practiceDetails?.intent,
          BusinessName: practiceDetails?.studioName,
          Website: practiceDetails?.website,
        });
        if (settled.status < 200 || settled.status >= 300) {
          fail((settled.body as WriteResponseBody | null)?.Message);
          return;
        }

        // Right after the write that carries this same mask - the same position the artist's own
        // emit sits in. Firing on every pass, not just the first, is the same cost the artist
        // branch above already accepts: a retry emits this event twice, once per successful write,
        // rather than once per account - the house's own shape, not something invented here.
        emitFullstoryEvent("OB: Intent Declared", {
          intents: analyticsLabelsFor(practiceDetails?.pickedLabels ?? [], COLLECTOR_GOALS),
        });
      } catch (_) {
        fail(undefined);
        return;
      }
      // Falls through to the /conversions block below, shared with the gallery except for the
      // BusinessName/Website pair the settings PUT above already delivered: that
      // block converts the ghost, fires the tag-gated step-4 transition (accountConversionType
      // returns "Collector" here, which is what makes conversionTracking.ts skip the Google Ads
      // tag - see that function's own doc comment), clears the journey, and moves to step 4. Its
      // error handling is the gallery's own `fail(body?.Message)` banner, not the artist's inline
      // emailError: the Collector has no legacy screen like CreateAccountScreen to have inherited
      // that treatment from, and /register mints the ghost with a GUID email, so it is /conversions
      // - this shared block - where a real duplicate would surface.
    }

    try {
      const { businessName, website } = businessFieldsFor(persona, details, practiceDetails);
      const response = await Request.post(conversionsPostUrl(), {
        Email: details.email,
        Name: details.fullName,
        Phone: details.phone,
        Password: details.password,
        HowDidYouHear: details.howDidYouHear,
        HasAgreedToTerms: details.hasAgreedToTerms,
        BusinessName: businessName,
        Website: website,
      });
      const body = response.body as ConversionsResponseBody | null;

      if (response.status >= 200 && response.status < 300) {
        emitAccountConverted();
        const nextFirstName = firstNameOf(details.fullName);
        // trackAccountConversion holds the step-4 transition until the tag's own callback fires (or
        // immediately, gtag missing/Collector) - the same "wait for the callback" contract as the
        // legacy redirect, now guarding a state change instead of a navigation.
        trackAccountConversion(accountConversionType(persona), () => {
          // Onboarding.jsx:487, inside the same "wait for the tag" callback (:492) - a second
          // signup in this browser must not reuse this journey's id. Not Onboarding.jsx:488's
          // `ac_welcome` cookie: its only reader requires Subscriptions.IsArtsySource, which no
          // v2 signup can be (v2 never sets props.isArtsy), so setting it here would be dead
          // weight this ticket was not asked to add.
          clearOnboardingJourney();
          accountSubmitting.current = false;
          setAccountLoading(false);
          setFirstName(nextFirstName);
          setStep(4);
        });
        return;
      }

      fail(body?.Message);
    } catch (_) {
      fail(undefined);
    }
  };

  // D5: the tracker's own capture, read off whichever step is currently active - the same
  // snapshot that step's own Back button would send through onBack, since a tracker click does
  // not fire it. Steps 1 and 4 hold nothing to capture (the picker has no fields; step 4 is
  // terminal and unreachable per the comment below).
  const captureActiveStep = () => {
    if (step === 2) {
      setPracticeDetails(practiceStepRef.current?.captureDetails());
    } else if (step === 3) {
      setAccountDetails(accountStepRef.current?.captureDetails());
    }
  };

  // Step 4 (Get started) is the one screen a submitted account cannot be walked back out of:
  // "Account" is a live record by then, and reopening it would re-POST conversions over it. Step
  // 1 (the picker) gets the same treatment once hasSession is true, and for the same shape of
  // reason: a session is a live identity, and offering a way back to a persona choice over one is
  // exactly the swap rule #4 exists to block - 3 -> 2 crosses nothing so it keeps working.
  //
  // Onboarding.jsx:647 blocked all navigation while `loading` by painting a full-screen
  // <ModalBackground /> over everything - this design has no such overlay, and the two
  // `disabled` props on Practice's and Account's own buttons do not reach the tracker, which
  // stays clickable during the registrations POST / settings PUT (practiceLoading) or the
  // conversions POST (accountLoading) unless it is told to stop being one here.
  const navigationBlocked = practiceLoading || accountLoading;
  // personaCopyFor's own doc comment (above accountConversionType) has the reasoning behind the
  // fallback this reads.
  const copy: PersonaCopy = personaCopyFor(persona);
  const steps: OnboardingV2Step[] = ONBOARDING_V2_STEP_LABELS.map((label, index) => {
    const stepNumber = index + 1;
    const reachable =
      step !== 4 &&
      stepNumber < step &&
      !(hasSession && stepNumber === 1) &&
      !navigationBlocked;
    return reachable
      ? {
          label,
          onSelect: () => {
            captureActiveStep();
            clearErrors();
            setStep(stepNumber as 1 | 2 | 3 | 4);
          },
        }
      : { label };
  });

  return (
    <OnboardingV2Stage steps={steps} activeStep={step} logoLinksToLogIn={step !== 4}>
      {step === 1 && (
        <PersonaPicker selected={persona} onSelect={handlePersonaSelect} />
      )}
      {step === 2 && (
        <GalleryPracticeStep
          ref={practiceStepRef}
          initialValues={practiceDetails}
          error={practiceError}
          loading={practiceLoading}
          goals={copy.goals}
          heading={copy.practiceHeading}
          backLabel={copy.practiceBackLabel}
          goalTestIdPrefix={copy.goalTestIdPrefix}
          practiceFields={copy.practiceFields}
          careerFields={copy.careerFields}
          orgRequired={copy.orgRequired}
          validateOrgNameFormat={copy.validateOrgNameFormat}
          sectionLabel={copy.practiceSectionLabel}
          orgLabel={copy.orgLabel}
          orgPlaceholder={copy.orgPlaceholder}
          siteLabel={copy.siteLabel}
          sitePlaceholder={copy.sitePlaceholder}
          onBack={
            hasSession
              ? undefined
              : (details) => {
                  // Same capture the tracker's own "You" link now goes through (D5): a 2 -> 1 -> 2
                  // round trip, via either route, must not throw typed answers away.
                  setPracticeDetails(details);
                  setPracticeError(undefined);
                  setStep(1);
                }
          }
          onContinue={handlePracticeContinue}
        />
      )}
      {step === 3 && (
        <GalleryAccountStep
          // This screen reads initialValues once, at mount, and with a session it mounts BEFORE
          // the settings GET answers (that ordering is deliberate - it is what keeps a failed GET
          // on this screen instead of back at the picker). So a ghost's own BusinessName/Website,
          // which arrive later, would never be adopted: the fields would keep showing whatever the
          // query string put there. Keying on the two values is what lets them in - and only when
          // they actually differ, so the common case (nothing to adopt) does not remount and does
          // not discard anything typed in the meantime.
          key={`account:${accountDetails?.galleryName ?? ""}|${accountDetails?.website ?? ""}`}
          ref={accountStepRef}
          initialValues={accountDetails}
          error={accountError}
          emailError={accountEmailError}
          loading={accountLoading}
          accountOrgFields={copy.accountOrgFields}
          validateFullNameFormat={copy.validateFullNameFormat}
          backLabel={copy.accountBackLabel}
          emailPlaceholder={copy.emailPlaceholder}
          orgLabel={copy.orgLabel}
          orgPlaceholder={copy.orgPlaceholder}
          siteLabel={copy.siteLabel}
          sitePlaceholder={copy.sitePlaceholder}
          onBack={(details) => {
            // Nothing was submitted, so nothing here is validated - it is just what the visitor
            // had typed, kept so a 3 -> 2 -> 3 round trip does not throw it away (D4).
            setAccountDetails(details);
            setAccountError(undefined);
            setAccountEmailError(undefined);
            setStep(2);
          }}
          onSubmit={handleAccountSubmit}
        />
      )}
      {step === 4 && <GetStartedStep firstName={firstName} actions={copy.getStartedActions} />}
    </OnboardingV2Stage>
  );
}
